import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Modal, Pagination, Select, SelectItem, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextArea, TextInput, Toggle } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { DoiMaDialog, useIsFounder } from '../components/MaDaiLy';

type Partner = {
  id: string; code: string; displayName: string; status: string; commissionRateBps: number;
  isSuperRoot: boolean; parent?: { id: string; code: string } | null;
  children: { id: string; code: string; displayName: string; status: string; commissionRateBps: number }[];
  balance?: { balance: string; vestingBalance: string; reservedBalance: string } | null;
};
type Direction = 'agentToMember' | 'memberToAgent';
// per-agent transfer switches (docs/specs/cong-tac-chuyen-tien.md); pending = he A has not applied the newest change yet
type TransferSwitch = { agentToMember: boolean; memberToAgent: boolean; pending: Direction[] };
type Detail = { partner: Partner; members: number; transferSwitch: TransferSwitch | null };
const CHIEU: [Direction, string][] = [['agentToMember', 'Đại lý chuyển cho thành viên'], ['memberToAgent', 'Thành viên chuyển cho đại lý']];
type Paged<T> = { items: T[]; total: number; page: number; pageSize: number };
type LedgerRow = { id: string; type: string; amount: string; createdAt: string; idempotencyKey: string };
type Member = { id: number; username: string; nickname: string | null; createdAt: string };

const ST: Record<string, string> = { ACTIVE: 'Hoạt động', PAUSED: 'Tạm dừng', BANNED: 'Khóa' };

const columns: ColumnDef<LedgerRow, any>[] = [
  { header: 'Loại', accessorKey: 'type' },
  { header: 'Số tiền', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Khóa', accessorKey: 'idempotencyKey', cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
];
const memCols: ColumnDef<Member, any>[] = [
  { header: 'Tên', accessorKey: 'username', cell: (c) => <Link to={'/users/' + c.row.original.id}>{c.getValue()}</Link> },
  { header: 'Nick', accessorKey: 'nickname', cell: (c) => c.getValue() ?? '-' },
  { header: 'ID', accessorKey: 'id', cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Gắn lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
];

// Server-paged list: page state plus the Carbon pager under the table
function usePaged<T>(key: string, id: string, path: string) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // another agent (parent / child link) starts on its first page
  useEffect(() => { setPage(1); }, [id]);
  const q = useQuery({
    queryKey: [key, id, page, pageSize],
    queryFn: () => api.get<Paged<T>>(path, { page, pageSize }),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
  const pager = q.data && (q.data.total > pageSize || page > 1) ? (
    <Pagination page={page} pageSize={pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total}
      onChange={({ page: p, pageSize: s }) => { setPage(p); setPageSize(s); }} />
  ) : null;
  return { q, pager };
}

export function AgentDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { can } = useAuth();
  const manage = can('agents.manage');
  const founder = useIsFounder();
  const q = useQuery({ queryKey: ['agent', id], queryFn: () => api.get<Detail>('/api/admin/agents/' + id), enabled: !!id });
  const ledger = usePaged<LedgerRow>('agent-ledger', id, '/api/admin/agents/' + id + '/ledger');
  const members = usePaged<Member>('agent-members', id, '/api/admin/agents/' + id + '/members');
  const [rateOpen, setRateOpen] = useState(false);
  const [rate, setRate] = useState('');
  const [codeOpen, setCodeOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [status, setStatus] = useState('ACTIVE');
  const [bindOpen, setBindOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState('');
  const patch = useMutation({
    mutationFn: (body: { rateBps?: number; status?: string }) => api.patch('/api/admin/agents/' + id, body),
    onSuccess: () => {
      setRateOpen(false); setStatusOpen(false);
      qc.invalidateQueries({ queryKey: ['agent', id] });
    },
  });
  const closeBind = () => { setBindOpen(false); setUserId(''); setForce(false); setReason(''); bind.reset(); };
  const bind = useMutation({
    mutationFn: () => api.post('/api/admin/agents/' + id + '/bind', {
      gameUserId: Number(userId), ...(force ? { force: true } : {}), ...(reason.trim() ? { reason: reason.trim() } : {}),
    }),
    onSuccess: () => { closeBind(); qc.invalidateQueries({ queryKey: ['agent', id] }); qc.invalidateQueries({ queryKey: ['agent-members', id] }); },
  });
  const postFtd = bind.error instanceof ApiError && bind.error.code === 'POST_FTD';
  const sw = useMutation({
    mutationFn: (body: { direction: Direction; on: boolean }) => api.post('/api/admin/agents/' + id + '/transfer-switch', body),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['agent', id] }); },
  });

  const back = <Link className="cell-link" to="/agents">← Danh sách đại lý</Link>;
  if (q.isLoading) return <div className="page"><PageHeader title="Đại lý" actions={back} /><BlockSkeleton lines={8} /></div>;
  if (q.error || !q.data) return <div className="page"><PageHeader title="Đại lý" actions={back} /><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const p = q.data.partner;
  return (
    <div className="page">
      <PageHeader title={p.displayName + ' · ' + p.code}
        lead={p.isSuperRoot ? 'Nhà cái: gốc của mọi tuyến, nhận phần còn lại tới mức trần.' : (p.parent ? <>Tuyến trên <Link to={'/agents/' + p.parent.id}>{p.parent.code}</Link></> : 'Không có tuyến trên')}
        actions={back} />
      <div className="row">
        <Tag size="sm" type={p.status === 'ACTIVE' ? 'green' : 'red'}>{ST[p.status] ?? p.status}</Tag>
        <span>Tỷ lệ <span className="num">{p.commissionRateBps}</span> bps</span>
        <span>Số dư <Money v={p.balance?.balance} /></span>
        <span>Chờ 72h <Money v={p.balance?.vestingBalance} /></span>
        <span>Giữ chi <Money v={p.balance?.reservedBalance} /></span>
        <span>Thành viên cây <span className="num">{q.data.members}</span></span>
      </div>
      {manage || (founder && !p.isSuperRoot) ? (
        <div className="row" style={{ marginTop: 12 }}>
          {founder && !p.isSuperRoot ? <Button size="sm" kind="secondary" onClick={() => setCodeOpen(true)}>Đổi mã</Button> : null}
          {manage ? (
            <>
              <Button size="sm" kind="secondary" onClick={() => { setRate(String(p.commissionRateBps)); setRateOpen(true); }}>Sửa tỷ lệ</Button>
              {p.isSuperRoot ? null : <Button size="sm" kind="secondary" onClick={() => { setStatus(p.status); setStatusOpen(true); }}>Đổi trạng thái</Button>}
              <Button size="sm" kind="tertiary" onClick={() => setBindOpen(true)}>Gắn thành viên</Button>
            </>
          ) : null}
        </div>
      ) : null}
      {q.data.transferSwitch ? (
        <section className="panel section" aria-label="Công tắc chuyển tiền" style={{ marginBottom: 16 }}>
          <div className="panel__head"><h2>Chuyển tiền của đại lý này</h2></div>
          <p className="muted">Tắt chiều nào thì chiều đó bị từ chối, kể cả khi công tắc hệ thống đang mở. Không ảnh hưởng đại lý cấp dưới.</p>
          <div className="row" style={{ gap: 32, marginTop: 8 }}>
            {CHIEU.map(([d, label]) => (
              <div key={d}>
                <Toggle id={'ag-ct-' + d} labelText={label} labelA="Tắt" labelB="Mở" toggled={q.data.transferSwitch![d]}
                  disabled={!manage || sw.isPending} onToggle={(on) => sw.mutate({ direction: d, on })} />
                {q.data.transferSwitch!.pending.includes(d) ? <Tag size="sm" type="gray">Đang chờ áp dụng</Tag> : null}
              </div>
            ))}
          </div>
          <InlineError error={sw.error} />
        </section>
      ) : null}
      <Tabs>
        <TabList aria-label="Chi tiết"><Tab>Sổ cái</Tab><Tab>Thành viên</Tab><Tab>Cấp dưới</Tab></TabList>
        <TabPanels>
          <TabPanel>
            <DataTable columns={columns} data={ledger.q.data?.items} loading={ledger.q.isLoading || ledger.q.isPlaceholderData} error={ledger.q.error}
              onRetry={() => ledger.q.refetch()} empty={{ title: 'Chưa có bút toán', body: 'Hoa hồng Rolling ghi vào đây sau khi ingest sổ cái game.' }} />
            {ledger.pager}
          </TabPanel>
          <TabPanel>
            <DataTable columns={memCols} data={members.q.data?.items} loading={members.q.isLoading || members.q.isPlaceholderData} error={members.q.error}
              onRetry={() => members.q.refetch()} empty={{ title: 'Chưa có thành viên', body: 'Người chơi gắn ref hoặc admin bind.' }} />
            {members.pager}
          </TabPanel>
          <TabPanel>
            <ul className="stack">
              {p.children.length === 0 ? <li className="muted">Không có cấp dưới.</li> : p.children.map((c) => (
                <li key={c.id}><Link to={'/agents/' + c.id}>{c.code}</Link> · {c.displayName} · <span className="num">{c.commissionRateBps}</span> bps · {ST[c.status] ?? c.status}</li>
              ))}
            </ul>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <Modal open={rateOpen} modalHeading="Sửa tỷ lệ Rolling (bps)" primaryButtonText="Lưu" secondaryButtonText="Hủy"
        primaryButtonDisabled={patch.isPending} onRequestClose={() => setRateOpen(false)} onRequestSubmit={() => patch.mutate({ rateBps: Number(rate) })}>
        <TextInput id="ag-rate" labelText="bps" helperText="Không vượt tỷ lệ tuyến trên, không thấp hơn đại lý con." value={rate}
          onChange={(e) => setRate(e.target.value.replace(/\D/g, ''))} />
        <InlineError error={patch.error} />
      </Modal>
      {p.isSuperRoot ? null : <DoiMaDialog partner={p} open={codeOpen} onClose={() => setCodeOpen(false)} />}
      <Modal open={statusOpen} modalHeading="Đổi trạng thái đại lý" primaryButtonText="Lưu" secondaryButtonText="Hủy"
        primaryButtonDisabled={patch.isPending} onRequestClose={() => setStatusOpen(false)} onRequestSubmit={() => patch.mutate({ status })}>
        <Select id="ag-st2" labelText="Trạng thái" value={status} onChange={(e) => setStatus(e.target.value)}>
          <SelectItem value="ACTIVE" text="Hoạt động" />
          <SelectItem value="PAUSED" text="Tạm dừng" />
          <SelectItem value="BANNED" text="Khóa" />
        </Select>
        <InlineError error={patch.error} />
      </Modal>
      <Modal open={bindOpen} modalHeading="Gắn người chơi vào đại lý" primaryButtonText={force ? 'Chuyển bắt buộc' : 'Gắn'} secondaryButtonText="Hủy"
        danger={force}
        primaryButtonDisabled={!userId || bind.isPending || (force && reason.trim().length < 5)} onRequestClose={closeBind} onRequestSubmit={() => bind.mutate()}>
        <div className="stack">
          <TextInput id="ag-uid" labelText="Mã người chơi (gameUserId)" value={userId} onChange={(e) => { setUserId(e.target.value.replace(/\D/g, '')); bind.reset(); }} />
          {postFtd || force ? (
            <>
              <Checkbox id="ag-force" labelText="Chuyển bắt buộc: người chơi đã nạp tiền lần đầu" checked={force}
                onChange={(_e, { checked }) => setForce(checked)} />
              <TextArea id="ag-reason" labelText="Lý do chuyển (bắt buộc)" rows={3} maxLength={500} value={reason}
                disabled={!force} onChange={(e) => setReason(e.target.value)} />
            </>
          ) : null}
          <InlineError error={bind.error} />
        </div>
      </Modal>
    </div>
  );
}
